"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

type Props = {
  children: React.ReactNode;
  className?: string;
  /**
   * If true, adds a subtle fade above the dock (nice on scrollable pages).
   */
  withTopFade?: boolean;
};

export function LiquidGlassDock({ children, className, withTopFade = true }: Props) {
  return (
    <div className={cn("relative", className)}>
      {withTopFade ? (
        <div className="absolute bottom-full left-0 right-0 h-20 bg-gradient-to-t from-background/80 via-background/20 to-transparent pointer-events-none" />
      ) : null}

      <div className="relative rounded-3xl overflow-hidden backdrop-blur-xl">
        {/* Base glass */}
        <div className="absolute inset-0 bg-white/70 dark:bg-black/60 backdrop-blur-2xl" />

        {/* Depth gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/20 to-white/40 dark:from-black/40 dark:via-black/20 dark:to-black/40" />

        {/* Frosted noise (light mode only) */}
        <div
          className="absolute inset-0 opacity-30 dark:opacity-0 mix-blend-soft-light"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C/svg%3E\")",
            backgroundSize: "200px 200px",
          }}
        />

        {/* Shimmer */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent dark:via-white/5 animate-glass-shimmer" />

        {/* Borders / highlights */}
        <div className="absolute inset-0 rounded-3xl border-2 border-gray-300/50 dark:border-white/10" />
        <div className="absolute inset-0 rounded-3xl shadow-[inset_0_2px_4px_0_rgba(255,255,255,0.5)] dark:shadow-[inset_0_1px_1px_0_rgba(255,255,255,0.05)]" />
        <div className="absolute inset-0 rounded-3xl shadow-[inset_0_-1px_2px_0_rgba(0,0,0,0.1)] dark:shadow-none" />
        <div className="absolute inset-0 rounded-3xl shadow-[0_8px_32px_0_rgba(0,0,0,0.08)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.5)]" />

        <div className="relative">{children}</div>
      </div>
    </div>
  );
}

// Keep the animation local and reusable
// (scoped via unique class name to avoid collisions).
export function LiquidGlassDockGlobalStyles() {
  return (
    // FP: jsx/global are styled-jsx props handled by Next.js SWC transform
    // oxlint-disable-next-line react-doctor/no-unknown-property
    <style jsx global>{`
      @keyframes glass-shimmer {
        0% {
          transform: translateX(-100%);
        }
        100% {
          transform: translateX(100%);
        }
      }

      .animate-glass-shimmer {
        animation: glass-shimmer 3s ease-in-out infinite;
      }
    `}</style>
  );
}

